import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { AdminPageHeader } from "@/components/AdminPageHeader"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { api } from "@/lib/client"
import { formatMoney } from "@/lib/format"
import { queryKeys } from "@/lib/queryKeys"
import { ADMIN_TABLE_CLASSNAME, ADMIN_TABLE_HEAD_TH, ADMIN_TABLE_ROW } from "@/lib/table"
import { type ProductKind } from "@/lib/types"

const CATALOG_PAGES: Record<
  string,
  { description: string; kind: ProductKind; title: string }
> = {
  "/catalog/ingredients": {
    description: "Recipe components pulled on prep",
    kind: "ingredient",
    title: "Ingredients",
  },
  "/catalog/packaging": {
    description: "Clamshells, labels, and packing materials",
    kind: "packaging",
    title: "Packaging",
  },
  "/catalog/kits": {
    description: "Custom boxes and bundled products",
    kind: "kit",
    title: "Kits",
  },
}

const MENU_PAGE = {
  description: "Sellable dishes, bowls, plates, and sides",
  kind: "finished_good" as const,
  title: "Menu",
}

function catalogPage(pathname: string) {
  return CATALOG_PAGES[pathname] ?? MENU_PAGE
}

export function ProductsPage() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const page = catalogPage(pathname)
  const queryClient = useQueryClient()
  const [q, setQ] = useState("")
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [sku, setSku] = useState("")
  const [price, setPrice] = useState("0")
  const filters = { kind: page.kind, q: q || undefined }
  const products = useQuery({
    queryFn: () => api.products(filters),
    queryKey: queryKeys.products(filters as Record<string, string>),
  })
  const showCategory = page.kind === "finished_good" || page.kind === "kit"
  const colSpan = showCategory ? 6 : 5

  const create = useMutation({
    mutationFn: () =>
      api.createProduct({
        kind: page.kind,
        name,
        price: Number(price),
        sku,
      }),
    onError: (err) => toast.error(err.message),
    onSuccess: async () => {
      toast.success("Product created")
      setOpen(false)
      setName("")
      setSku("")
      await queryClient.invalidateQueries({ queryKey: ["products"] })
    },
  })

  return (
    <div>
      <AdminPageHeader description={page.description} title={page.title}>
        <Input className="w-48" onChange={(e) => setQ(e.target.value)} placeholder="Search" value={q} />
        <Button onClick={() => setOpen(true)} size="sm" type="button">
          New product
        </Button>
      </AdminPageHeader>
      <div className="overflow-hidden rounded-lg border border-border">
        <Table className={ADMIN_TABLE_CLASSNAME}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Name</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>SKU</TableHead>
              {showCategory ? <TableHead className={ADMIN_TABLE_HEAD_TH}>Category</TableHead> : null}
              <TableHead className={`${ADMIN_TABLE_HEAD_TH} text-right`}>Price</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Variants</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.isPending ? (
              <TableRow>
                <TableCell className="text-muted-foreground" colSpan={colSpan}>
                  Loading…
                </TableCell>
              </TableRow>
            ) : (products.data?.products ?? []).length === 0 ? (
              <TableRow>
                <TableCell className="text-muted-foreground" colSpan={colSpan}>
                  No products match.
                </TableCell>
              </TableRow>
            ) : (
              (products.data?.products ?? []).map((product) => (
                <TableRow
                  className={ADMIN_TABLE_ROW}
                  key={product.id}
                  onClick={() => navigate(`${pathname}/${product.id}`)}
                >
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2.5">
                      {product.imageUrlLight ? (
                        <img
                          alt=""
                          className="size-9 shrink-0 rounded-md border border-border object-cover"
                          src={product.imageUrlLight}
                        />
                      ) : null}
                      {product.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{product.sku}</TableCell>
                  {showCategory ? <TableCell>{product.categoryName || "—"}</TableCell> : null}
                  <TableCell className="text-right">{formatMoney(product.price, product.currency)}</TableCell>
                  <TableCell>{product.variantCount ?? "—"}</TableCell>
                  <TableCell>{product.active ? "Yes" : "No"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog onOpenChange={setOpen} open={open}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New product</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="pname">Name</Label>
              <Input id="pname" onChange={(e) => setName(e.target.value)} value={name} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="psku">SKU</Label>
              <Input id="psku" onChange={(e) => setSku(e.target.value)} value={sku} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pprice">Price</Label>
              <Input id="pprice" onChange={(e) => setPrice(e.target.value)} type="number" value={price} />
            </div>
          </div>
          <DialogFooter>
            <Button disabled={!name || !sku || create.isPending} onClick={() => create.mutate()} type="button">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
